import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/*
 * ---------------------------------------------------------------------------
 * Ethnicvilla speed patch (based on Horizon 4.2.0 paginated-list.js)
 *
 * Same behaviour and look as the stock file, with these changes:
 *  1. Next products start loading ~1600px BEFORE the shopper reaches the end
 *     of the grid (stock: 100px), so they are already there when they scroll.
 *  2. Fixes a stall: if the "load more" trigger is still on screen after a page
 *     is added (short pages, zoomed-out grid view), the next page now loads too.
 *  3. A failed or throttled request no longer freezes infinite scroll for the
 *     rest of the visit — it retries (max 3 times) instead.
 *  4. Prevents the same page from being added twice when the shopper scrolls fast.
 *  5. Once the shopper starts scrolling, it keeps TWO pages ready ahead instead
 *     of one, so slow mobile data has twice as long to deliver them.
 *  6. The first "next page" prefetch waits until the browser is idle (max 1.5s)
 *     so it doesn't compete with the first screen of images, and later
 *     prefetches are no longer delayed indefinitely by busy app scripts.
 *  7. Results loaded for an old filter can't be shown after the filter changes.
 * ---------------------------------------------------------------------------
 */

/** Top margin stays at the stock 100px; bottom margin pre-loads the next page early. */
const INFINITE_SCROLL_ROOT_MARGIN = '100px 0px 1600px 0px';

/** Longest we wait for an idle moment before prefetching anyway (ms). */
const INITIAL_PREFETCH_TIMEOUT = 1500;
const NEXT_PREFETCH_TIMEOUT = 600;

/** How many times a failed page request is retried before giving up. */
const MAX_FETCH_RETRIES = 3;

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  /** Guards against the same page being rendered twice at once. */
  #isRenderingNext = false;
  #isRenderingPrevious = false;

  /** Pages whose last response was unusable — refetch them without the cache. */
  /** @type {Set<number>} */
  #pagesToRefetch = new Set();

  #retryCount = 0;

  /** Bumped whenever filters/sorting change, so late responses for old results are ignored. */
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // Prefetch the neighbouring pages as soon as the browser has a spare moment,
    // so the request doesn't compete with the first screen of product images.
    requestIdleCallback(
      () => {
        if (!this.isConnected) return;
        this.#fetchPage('next');
        this.#fetchPage('previous');
      },
      { timeout: INITIAL_PREFETCH_TIMEOUT }
    );
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: INFINITE_SCROLL_ROOT_MARGIN,
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Re-checks a trigger element. IntersectionObserver only fires when visibility
   * *changes*, so if the trigger is still in range after new products were added,
   * nothing would happen. Re-observing forces a fresh check.
   * @param {Element | undefined} element
   */
  #recheckTrigger(element) {
    if (!element || !this.infinityScrollObserver || !this.isConnected) return;
    this.infinityScrollObserver.unobserve(element);
    this.infinityScrollObserver.observe(element);
  }

  /**
   * Retries loading after a failed request, backing off a little each time.
   * @param {"previous" | "next"} type
   */
  #scheduleRetry(type) {
    if (this.#retryCount >= MAX_FETCH_RETRIES) return;
    this.#retryCount++;

    setTimeout(() => {
      const { viewMorePrevious, viewMoreNext } = this.refs;
      this.#recheckTrigger(type === 'next' ? viewMoreNext : viewMorePrevious);
    }, 1000 * this.#retryCount);
  }

  /**
   * Keeps `this.refs` (the list of cards) in sync right after we add cards, instead of
   * waiting for the idle-time MutationObserver. Without this, a fast scroller on a busy
   * page could get the same page added twice.
   */
  #syncRefs() {
    this.updatedCallback?.();
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      // Network hiccup (common on mobile data). Don't leave the page waiting forever —
      // the render step below will notice the missing page and retry.
    }

    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    // Skip the cache if the previous response for this page was unusable (e.g. throttled).
    const useCache = !this.#pagesToRefetch.has(pageNumber);
    const generation = this.#generation;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, useCache, pageInfo.url);

    // Filters changed while this was loading — these products belong to the old results.
    if (generation !== this.#generation) return;

    this.#pagesToRefetch.delete(pageNumber);
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * Prefetches the page AFTER the next one, so two pages are always ready ahead.
   */
  #prefetchAhead() {
    const next = this.#getPage('next');
    if (!next) return;

    const aheadPage = next.page + 1;
    if (this.pages.has(aheadPage)) return;

    const aheadUrl = new URL(next.url);
    aheadUrl.searchParams.set('page', aheadPage.toString());
    this.#fetchSpecificPage(aheadPage, aheadUrl).catch(() => {});
  }

  /**
   * Marks a page as needing a fresh request on the next attempt.
   * @param {number} pageNumber
   */
  #markForRefetch(pageNumber) {
    this.pages.delete(pageNumber);
    this.#pagesToRefetch.add(pageNumber);
  }

  async #renderNextPage() {
    if (this.#isRenderingNext) return;

    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    this.#isRenderingNext = true;
    let appended = false;

    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) {
          this.#markForRefetch(nextPage.page);
          this.#scheduleRetry('next');
          return;
        }
      }

      grid.append(...nextPageItemElements);
      appended = true;
      this.#retryCount = 0;
      this.#syncRefs();

      this.#aspectRatioHelper?.processNewElements();

      await yieldToMainThread();

      history.pushState('', '', nextPage.url.toString());

      requestIdleCallback(
        () => {
          this.#fetchPage('next');
          this.#prefetchAhead();
        },
        { timeout: NEXT_PREFETCH_TIMEOUT }
      );
    } finally {
      this.#isRenderingNext = false;
    }

    if (appended) this.#recheckTrigger(this.refs.viewMoreNext);
  }

  async #renderPreviousPage() {
    if (this.#isRenderingPrevious) return;

    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    this.#isRenderingPrevious = true;
    let prepended = false;

    try {
      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) {
          this.#markForRefetch(previousPage.page);
          this.#scheduleRetry('previous');
          return;
        }
      }

      // Store the current scroll position and height of the first element
      const currentScrollTop = getScrollTop();
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);
      prepended = true;
      this.#retryCount = 0;
      this.#syncRefs();

      this.#aspectRatioHelper?.processNewElements();

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
        const heightDiff = newHeight - oldHeight;
        scrollTo({
          top: currentScrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      await yieldToMainThread();

      history.pushState('', '', previousPage.url.toString());

      requestIdleCallback(
        () => {
          this.#fetchPage('previous');
        },
        { timeout: NEXT_PREFETCH_TIMEOUT }
      );
    } finally {
      this.#isRenderingPrevious = false;
    }

    if (prepended) this.#recheckTrigger(this.refs.viewMorePrevious);
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();
    this.#pagesToRefetch.clear();
    this.#retryCount = 0;
    this.#generation++;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
